import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, MapPin, Palette, Shield, MoreHorizontal } from 'lucide-react';
import FeaturedLayout from '../layouts/FeaturedLayout';
import {
  Card, CardContent, Badge, Button, Input, Label,
  Alert, AlertDescription, ImageUploader,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import { Textarea } from '../ui/textarea';
import {
  getAllEquipos, createEquipo, updateEquipo, deleteEquipo,
  getJuegosByEquipo, replaceEquipoInJuegos, findEquipoByNormalizedName
} from '../../lib/supabase';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';

const EquiposManager = () => {
  const { alert, ConfirmDialog } = useConfirmDialog();
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEquipo, setEditingEquipo] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '', nombre_corto: '', logo_url: '', ciudad: '', estado: '',
    color_primario: '#000000', color_secundario: '#FFFFFF', notas: ''
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [nameValidation, setNameValidation] = useState({ checking: false, duplicate: false, message: '' });
  const guard = useUnsavedGuard({ onClose: () => setShowModal(false) });

  // Delete state
  const [deleteModal, setDeleteModal] = useState({ open: false, equipo: null, juegos: [], action: 'keep', replacementId: '', loading: false });

  useEffect(() => { loadEquipos(); }, []);

  // Name validation debounce
  useEffect(() => {
    if (!showModal) return;
    const nombre = formData.nombre?.trim() || '';
    if (nombre.length < 2) { setNameValidation({ checking: false, duplicate: false, message: '' }); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setNameValidation((p) => ({ ...p, checking: true }));
      try {
        const dup = await findEquipoByNormalizedName(nombre, { excludeId: editingEquipo?.id || null });
        if (!cancelled) setNameValidation({ checking: false, duplicate: Boolean(dup), message: dup ? 'Ya existe un equipo con ese nombre.' : '' });
      } catch { if (!cancelled) setNameValidation({ checking: false, duplicate: false, message: '' }); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [formData.nombre, editingEquipo?.id, showModal]);

  const loadEquipos = async () => {
    try {
      setLoading(true);
      const { data, error } = await getAllEquipos();
      if (error) throw error;
      setEquipos(data || []);
    } catch { } finally { setLoading(false); }
  };

  const handleOpenModal = (equipo = null) => {
    setEditingEquipo(equipo);
    setFormData(equipo
      ? { nombre: equipo.nombre || '', nombre_corto: equipo.nombre_corto || '', logo_url: equipo.logo_url || '', ciudad: equipo.ciudad || '', estado: equipo.estado || '', color_primario: equipo.color_primario || '#000000', color_secundario: equipo.color_secundario || '#FFFFFF', notas: equipo.notas || '' }
      : { nombre: '', nombre_corto: '', logo_url: '', ciudad: '', estado: '', color_primario: '#000000', color_secundario: '#FFFFFF', notas: '' }
    );
    setFormError(null);
    setNameValidation({ checking: false, duplicate: false, message: '' });
    guard.resetDirty();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre.trim()) { setFormError('El nombre es obligatorio'); return; }
    if (nameValidation.duplicate) { setFormError('Ya existe un equipo con ese nombre.'); return; }
    try {
      setSaving(true); setFormError(null);
      const dup = await findEquipoByNormalizedName(formData.nombre, { excludeId: editingEquipo?.id || null });
      if (dup) throw new Error('Ya existe un equipo con ese nombre.');
      const { error } = editingEquipo
        ? await updateEquipo(editingEquipo.id, formData)
        : await createEquipo({ ...formData, activo: true });
      if (error) throw error;
      await loadEquipos();
      guard.resetDirty();
      setShowModal(false);
    } catch (err) {
      setFormError(err.message || 'Error al guardar el equipo');
    } finally { setSaving(false); }
  };

  const handleDelete = async (equipo) => {
    try {
      const juegos = await getJuegosByEquipo(equipo.id);
      setDeleteModal({ open: true, equipo, juegos, action: 'keep', replacementId: '', loading: false });
    } catch {
      await alert('Error', 'No se pudo revisar el uso de este equipo.');
    }
  };

  const handleConfirmDelete = async () => {
    const { equipo, juegos, action, replacementId } = deleteModal;
    if (!equipo) return;
    if (action === 'replace' && !replacementId) { await alert('Error', 'Selecciona el equipo de reemplazo.'); return; }
    try {
      setDeleteModal((p) => ({ ...p, loading: true }));
      if (action === 'replace' && juegos.length > 0) await replaceEquipoInJuegos(equipo.id, replacementId);
      await deleteEquipo(equipo.id);
      await loadEquipos();
      setDeleteModal({ open: false, equipo: null, juegos: [], action: 'keep', replacementId: '', loading: false });
    } catch (err) {
      await alert('Error', err.message || 'No se pudo desactivar el equipo.');
      setDeleteModal((p) => ({ ...p, loading: false }));
    }
  };

  const filteredEquipos = equipos.filter((eq) => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return eq.nombre?.toLowerCase().includes(t) || eq.ciudad?.toLowerCase().includes(t) || eq.nombre_corto?.toLowerCase().includes(t);
  });

  return (
    <FeaturedLayout title="Equipos rivales" subtitle="Los rivales de los torneos que organiza tu club. En un torneo externo los equipos los pone la organizadora." refreshAction={loadEquipos}>
      {/* Toolbar */}
      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="Buscar equipo..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <span className="text-sm text-muted-foreground hidden sm:block">
          <span className="font-medium text-foreground">{filteredEquipos.length}</span> equipos
        </span>
        <Button onClick={() => handleOpenModal()} className="h-10" aria-label="Nuevo equipo" title="Nuevo equipo">
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nuevo Equipo</span>
        </Button>
      </div>

      {/* Lista */}
      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando equipos...</p>
      ) : filteredEquipos.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Shield className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-2">{searchTerm ? 'No se encontraron equipos' : 'No hay equipos registrados'}</p>
            {!searchTerm && <Button size="sm" className="mt-2" onClick={() => handleOpenModal()}><Plus className="h-4 w-4 mr-1" /> Crear primer equipo</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filteredEquipos.map((equipo) => (
            <Card key={equipo.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-4 pb-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{equipo.nombre}</p>
                    {equipo.nombre_corto && <p className="text-xs text-muted-foreground">{equipo.nombre_corto}</p>}
                  </div>
                  {equipo.logo_url ? (
                    <img src={equipo.logo_url} alt={equipo.nombre} className="h-10 w-10 rounded object-contain bg-muted border shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded bg-muted border flex items-center justify-center shrink-0">
                      <Shield className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {(equipo.ciudad || equipo.estado) && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {[equipo.ciudad, equipo.estado].filter(Boolean).join(', ')}
                    </span>
                  )}
                  {equipo.color_primario && (
                    <span className="flex items-center gap-1">
                      <div className="h-4 w-4 rounded-full border" style={{ backgroundColor: equipo.color_primario }} />
                      {equipo.color_secundario && <div className="h-4 w-4 rounded-full border" style={{ backgroundColor: equipo.color_secundario }} />}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 pt-1.5 border-t">
                  <Button size="sm" className="h-8 text-xs flex-1" onClick={() => handleOpenModal(equipo)}>
                    <Edit2 className="h-3.5 w-3.5 mr-1.5" /> Editar
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" title="Mas acciones">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleDelete(equipo)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Desactivar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal crear/editar */}
      <Dialog open={showModal} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEquipo ? 'Editar Equipo' : 'Nuevo Equipo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}

            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} placeholder="Ej: Águilas del Norte" required />
              <div className="min-h-[1rem] text-xs">
                {nameValidation.checking ? <span className="text-muted-foreground">Validando...</span>
                  : nameValidation.duplicate ? <span className="text-destructive">{nameValidation.message}</span>
                  : formData.nombre.trim().length >= 2 ? <span className="text-emerald-600">Nombre disponible</span> : null}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nombre Corto</Label>
              <Input value={formData.nombre_corto} onChange={(e) => setFormData({ ...formData, nombre_corto: e.target.value })} placeholder="Ej: Águilas" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Ciudad</Label>
                <Input value={formData.ciudad} onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })} placeholder="Ej: Monterrey" />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Input value={formData.estado} onChange={(e) => setFormData({ ...formData, estado: e.target.value })} placeholder="Ej: Nuevo León" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Color Primario</Label>
                <div className="flex gap-2">
                  <Input type="color" value={formData.color_primario} onChange={(e) => setFormData({ ...formData, color_primario: e.target.value })} className="h-9 w-14 p-1" />
                  <Input value={formData.color_primario} onChange={(e) => setFormData({ ...formData, color_primario: e.target.value })} className="flex-1" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Color Secundario</Label>
                <div className="flex gap-2">
                  <Input type="color" value={formData.color_secundario} onChange={(e) => setFormData({ ...formData, color_secundario: e.target.value })} className="h-9 w-14 p-1" />
                  <Input value={formData.color_secundario} onChange={(e) => setFormData({ ...formData, color_secundario: e.target.value })} className="flex-1" />
                </div>
              </div>
            </div>
            <ImageUploader value={formData.logo_url} onChange={(url) => { guard.markDirty(); setFormData({ ...formData, logo_url: url }); }} bucket="logos" folder="equipos" label="Logo del Equipo" />
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={formData.notas} onChange={(e) => setFormData({ ...formData, notas: e.target.value })} rows={2} placeholder="Notas adicionales..." />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => guard.onOpenChange(false)} disabled={saving}>Cancelar</Button>
              <Button type="submit" disabled={saving || nameValidation.checking || nameValidation.duplicate}>
                {saving ? 'Guardando...' : editingEquipo ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>

      {/* Modal de desactivar con reemplazo */}
      <Dialog open={deleteModal.open} onOpenChange={(v) => { if (!v && !deleteModal.loading) setDeleteModal((p) => ({ ...p, open: false })); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Desactivar equipo</DialogTitle>
          </DialogHeader>
          {deleteModal.equipo && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Desactivar "{deleteModal.equipo.nombre}" lo quitará de futuras selecciones.
              </p>

              {deleteModal.juegos.length > 0 ? (
                <>
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    Este equipo está asignado a {deleteModal.juegos.length} juego(s).
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 text-sm cursor-pointer">
                      <input type="radio" className="mt-1" checked={deleteModal.action === 'keep'}
                        onChange={() => setDeleteModal((p) => ({ ...p, action: 'keep', replacementId: '' }))} />
                      <span>Mantener juegos como están</span>
                    </label>
                    <label className="flex items-start gap-3 text-sm cursor-pointer">
                      <input type="radio" className="mt-1" checked={deleteModal.action === 'replace'}
                        onChange={() => setDeleteModal((p) => ({ ...p, action: 'replace' }))} />
                      <span>Reemplazar por otro equipo en esos juegos</span>
                    </label>
                  </div>
                  {deleteModal.action === 'replace' && (
                    <div className="space-y-2">
                      <Label>Equipo de reemplazo</Label>
                      <Select value={deleteModal.replacementId} onValueChange={(v) => setDeleteModal((p) => ({ ...p, replacementId: v }))}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar equipo" /></SelectTrigger>
                        <SelectContent>
                          {equipos.filter((e) => e.id !== deleteModal.equipo.id).map((e) => (
                            <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">Este equipo no tiene juegos asignados.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal((p) => ({ ...p, open: false }))} disabled={deleteModal.loading}>Cancelar</Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={deleteModal.loading}>
              {deleteModal.loading ? 'Procesando...' : 'Desactivar equipo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </FeaturedLayout>
  );
};

export default EquiposManager;
